import React from 'react';
import { DefaultRootState, useSelector } from 'react-redux';

import { getBrandDomain } from 'utils/getBrandDomain';

export type i18n = {
  translate: {
    (key: string, allowEmpty?: boolean): React.ReactNode;
    toText: (key: string, allowEmpty?: boolean) => string;
  };
  translateToText: (key: string, allowEmpty?: boolean) => string;
};

const i18nPrefix = getBrandDomain() === 'xbo.com' ? '' : 'eu:';

export const useI18n = (): i18n => {
  const { labels = {} } = useSelector((state: DefaultRootState) => state.common.resources);

  const translate: i18n['translate'] = React.useMemo(() => {
    const translateFn: i18n['translate'] = (key, allowEmpty = false) => {
      const keyWithDomainPrefix = `${i18nPrefix}${key}`;

      if (!labels) return `#${keyWithDomainPrefix}`;

      if (!labels[key.toLowerCase()] && !labels[keyWithDomainPrefix.toLowerCase()] && allowEmpty)
        return <span data-test-label={keyWithDomainPrefix} />;

      return (
        <span data-test-label={keyWithDomainPrefix}>
          {labels[keyWithDomainPrefix.toLowerCase()] || labels[key.toLowerCase()] || `#${keyWithDomainPrefix}`}
        </span>
      );
    };

    translateFn.toText = (key, allowEmpty = false) => {
      const keyWithDomainPrefix = `${i18nPrefix}${key}`;
      if (!labels) return `#${key}`;
      if (!labels[key.toLowerCase()] && allowEmpty) return '';
      return labels[keyWithDomainPrefix.toLowerCase()] || labels[key.toLowerCase()] || `#${keyWithDomainPrefix}`;
    };

    return translateFn;
  }, [labels]);

  const result = React.useMemo(() => ({ translate, translateToText: translate.toText }), [translate]);

  return result;
};
